/**
 * ExperienceFeedbackCard — card discreto de feedback in-app.
 * Ativos (3+ dias de uso): estrelas + mensagem. Desistentes (trial expirado): + motivo.
 * Recompensa: +7 dias de teste + convite para planos e novidades.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, X } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REASON_OPTIONS = [
  { key: 'preco', label: '💰 Preço' },
  { key: 'dificuldade', label: '😵 Dificuldade de usar' },
  { key: 'faltou_funcionalidade', label: '🧩 Faltou funcionalidade' },
  { key: 'nao_era_o_que_esperava', label: '🤔 Não era o que eu esperava' },
  { key: 'outro', label: '✏️ Outro' },
];

export const StarRating = ({ rating, setRating }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="feedback-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`feedback-star-${n}`}
          onClick={() => setRating(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5"
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              n <= (hover || rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export const FeedbackSuccess = ({ result, onSeePlans, onClose }) => {
  const granted = result?.reward?.granted;
  const reactivated = result?.reward?.reactivated;
  return (
    <div className="bg-green-50 border border-green-300 rounded-lg p-5 mb-4" data-testid="feedback-success">
      <p className="font-bold text-green-800 text-lg">Obrigado pelo seu feedback! 🙏</p>
      {granted && (
        <p className="text-green-700 mt-1 font-semibold" data-testid="feedback-reward-msg">
          🎁 Você ganhou <span className="underline">+{result.reward.days} dias de teste grátis</span>
          {reactivated ? ' — seu acesso foi reativado a partir de hoje!' : '!'}
        </p>
      )}
      <p className="text-sm text-green-800 mt-3 font-semibold">Aproveite para conhecer as novidades:</p>
      <ul className="text-sm text-green-700 mt-1 space-y-1">
        {(result?.novidades || []).map((n, i) => (
          <li key={i}>{n}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
        {onSeePlans && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={onSeePlans} data-testid="feedback-see-plans">
            Conhecer os planos
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onClose} data-testid="feedback-close-success">
          {granted ? 'Continuar usando' : 'Fechar'}
        </Button>
      </div>
    </div>
  );
};

const ExperienceFeedbackCard = ({ onSeePlans }) => {
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/feedback/eligibility`)
      .then((r) => {
        if (r.data?.show) setInfo(r.data);
      })
      .catch(() => {});
  }, []);

  if (!info) return null;
  const desistente = info.mode === 'desistente';

  const dismiss = () => {
    setInfo(null);
    axios.post(`${API}/feedback/dismiss`).catch(() => {});
  };

  const closeSuccess = () => {
    const granted = result?.reward?.granted;
    setInfo(null);
    if (granted) window.location.reload();
  };

  const submit = async () => {
    if (!rating && !reason && !message.trim()) {
      toast.error('Dê uma nota, escolha um motivo ou escreva uma mensagem');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/feedback/submit`, {
        rating: rating || null,
        reason: reason || null,
        message,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar feedback');
    } finally {
      setSending(false);
    }
  };

  if (result) return <FeedbackSuccess result={result} onSeePlans={onSeePlans} onClose={closeSuccess} />;

  return (
    <div
      className={`relative border rounded-lg p-4 mb-4 ${
        desistente ? 'bg-orange-50 border-orange-300' : 'bg-amber-50 border-amber-200'
      }`}
      data-testid="feedback-card"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        data-testid="feedback-dismiss"
        aria-label="Fechar"
      >
        <X className="w-4 h-4" />
      </button>
      <p className="font-bold text-gray-800 text-center sm:text-left">
        {desistente ? 'Seu período de teste terminou — nos conte o que aconteceu 💬' : 'Como está sua experiência? 💬'}
      </p>
      {info.reward_days > 0 && (
        <p className="text-xs text-green-700 font-semibold mt-0.5 text-center sm:text-left">
          🎁 Responda e ganhe +{info.reward_days} dias de teste grátis
          {desistente ? ' (seu acesso volta na hora!)' : ''}
        </p>
      )}
      <div className="mt-3 space-y-3">
        <div className="flex justify-center sm:justify-start">
          <StarRating rating={rating} setRating={setRating} />
        </div>
        {desistente && (
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start" data-testid="feedback-reasons">
            {REASON_OPTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`feedback-reason-${r.key}`}
                onClick={() => setReason(reason === r.key ? '' : r.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  reason === r.key
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Conte sua dificuldade, experiência, crítica ou sugestão..."
          className="w-full border rounded-md p-2 text-sm bg-white min-h-[70px]"
          data-testid="feedback-message"
        />
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          onClick={submit}
          disabled={sending}
          data-testid="feedback-submit"
        >
          {sending ? 'Enviando...' : 'Enviar feedback'}
        </Button>
      </div>
    </div>
  );
};

export default ExperienceFeedbackCard;
